/**
 * Modern Bold · <mb-variant-picker> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * PDP variant resolver + UI sync. Owns:
 *   • radio change → variant lookup → updateUI (price, CTA, sticky CTA,
 *     stock urgency, swatch / size labels, SR announcements)
 *   • per-option-value availability probing (greys out unbuyable choices)
 *   • qty stepper (+ / −)
 *   • URL `?variant=` sync (replaceState)
 *   • dispatches `mb:variant-change` on the document so dependent widgets
 *     (modern-product-bundle, ...) can react without scraping the DOM
 *
 * Depends on:
 *   <product-form>        defined by modern-product-form.js — must load FIRST
 *   window.mbFormatMoney  defined by modern-bold.js — integer cents → "$x.yz"
 *
 * DOM contract (rendered by snippets/modern-product-detail.liquid plus the
 * embedded variant JSON in sections/pdp-grid-product-detail.liquid):
 *   <mb-variant-picker>
 *     <script type="application/json" data-mb-variants> [variants…]
 *     <product-form data-mb-product-form>
 *       <form>
 *         <input data-mb-variant-id>
 *         <input data-mb-qty-hidden>
 *         <input data-mb-option-input data-option-position="1|2|3" data-shape> × N
 *         [data-mb-shape-group] > <input data-mb-shape-input> × N  (multi-shape rugs only)
 *         [data-mb-shape-value]
 *         <button data-mb-add>
 *     [data-mb-price] [data-mb-compare-price]
 *     [data-mb-save-panel] > [data-mb-save-badge] + [data-mb-save-amount]
 *     [data-mb-sr-status]  (aria-live)
 *     [data-mb-stock-status] [data-mb-stock-text]
 *     [data-mb-option-value data-option-position="1|2|3"] × N
 *     [data-mb-qty-display] [data-mb-qty-minus] [data-mb-qty-plus]
 *
 * Sticky CTA refs (data-mb-sticky-*) live OUTSIDE this element (the
 * sticky bar is a section sibling, position:fixed) so they're queried
 * from `document` and null-guarded throughout updateUI.
 */
if (!customElements.get('mb-variant-picker')) {
  class MBVariantPicker extends HTMLElement {
    #cleanups = [];
    #variants = [];
    #inventory = {};
    #form = null;
    #refs = {};

    connectedCallback() {
      const variantDataEl = this.querySelector('[data-mb-variants]');
      if (!variantDataEl) return;
      try {
        this.#variants = JSON.parse(variantDataEl.textContent);
      } catch {
        return;
      }

      // Low-stock inventory map (variant id → qty). product.variants|json omits
      // inventory_quantity, so the picker can't recompute the "Only N left"
      // badge on variant change without this. Optional — absent/malformed just
      // means the urgency badge won't re-show after a change (graceful).
      const invDataEl = this.querySelector('[data-mb-inventory]');
      if (invDataEl) {
        try { this.#inventory = JSON.parse(invDataEl.textContent); } catch { this.#inventory = {}; }
      }

      const productFormEl = this.querySelector('[data-mb-product-form]');
      if (!productFormEl) return;
      this.#form = productFormEl.querySelector('form') || productFormEl;

      this.#cacheRefs();
      this.#bindForm();
      this.#bindShapes();
      this.#bindQty();
      this.#bindStickyChange();
      this.#bindStickyVisibility();

      // Initial pass — handles URL-preselected sold-out variants and
      // products where some option values are entirely unavailable on
      // first paint. Without this, the disabled CSS only kicks in after
      // the first user interaction.
      this.#updateOptionAvailability();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    // ── DOM cache ──────────────────────────────────────────────────────────

    #cacheRefs() {
      const $ = (sel) => this.querySelector(sel);
      this.#refs = {
        variantIdInput: this.#form.querySelector('[data-mb-variant-id]'),
        qtyHiddenInput: this.#form.querySelector('[data-mb-qty-hidden]'),
        qtyDisplayInput: $('[data-mb-qty-display]'),
        addBtn:    this.#form.querySelector('[data-mb-add]'),
        priceNow:  $('[data-mb-price]'),
        priceWas:  $('[data-mb-compare-price]'),
        savePanel:  $('[data-mb-save-panel]'),
        saveBadge:  $('[data-mb-save-badge]'),
        saveAmount: $('[data-mb-save-amount]'),
        srStatus:  $('[data-mb-sr-status]'),
        stockEl:   $('[data-mb-stock-status]'),
        stockText: $('[data-mb-stock-text]'),
        optionLabels: this.querySelectorAll('[data-mb-option-value]'),
        // Sticky CTA — may live OUTSIDE this element (the sticky bar is
        // rendered as a section sibling, position:fixed). Querying from
        // document keeps it reachable. Null-guarded throughout updateUI.
        stickyPrice:   document.querySelector('[data-mb-sticky-price]'),
        stickyWas:     document.querySelector('[data-mb-sticky-was]'),
        stickyVariant: document.querySelector('[data-mb-sticky-variant]'),
        stickyAdd:     document.querySelector('[data-mb-sticky-add]'),
        stickyImage:   document.querySelector('[data-mb-sticky-image]'),
      };
    }

    // ── Variant selection ──────────────────────────────────────────────────

    #getSelectedOptions() {
      const opts = {};
      this.#form.querySelectorAll('[data-mb-option-input]:checked').forEach((inp) => {
        opts[parseInt(inp.dataset.optionPosition, 10)] = inp.value;
      });
      return opts;
    }

    #findVariant(selectedOptions) {
      return this.#variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        for (const [pos, val] of Object.entries(selectedOptions)) {
          if (opts[parseInt(pos, 10) - 1] !== val) return false;
        }
        return true;
      }) || null;
    }

    /**
     * Probe each option-value for purchasability against the current other-
     * position selections. Disabled radios get the native `disabled` attr
     * so clicks are swallowed and the strike-through CSS kicks in. The
     * currently-checked input stays in the probe (sold-out URL preselects
     * should still appear "selected + struck-through").
     *
     * The INITIAL state is server-rendered (see the `buyable` lookup in
     * snippets/modern-product-detail.liquid) — this runs on mount only to
     * confirm it, then on every change to recompute. Don't drop the
     * server-side pass: without it the page paints every value as
     * available until this deferred script runs.
     */
    #updateOptionAvailability() {
      const selected = this.#getSelectedOptions();
      this.#form.querySelectorAll('[data-mb-option-input]').forEach((inp) => {
        const pos = parseInt(inp.dataset.optionPosition, 10);
        const value = inp.value;
        const probe = Object.assign({}, selected, { [pos]: value });

        const anyAvailable = this.#variants.some((v) => {
          const opts = [v.option1, v.option2, v.option3];
          for (const p in probe) {
            if (opts[parseInt(p, 10) - 1] !== probe[p]) return false;
          }
          return v.available;
        });

        inp.disabled = !anyAvailable;
        const label = inp.closest('label');
        if (label) label.classList.toggle('is-unavailable', !anyAvailable);
      });
    }

    // ── UI sync ────────────────────────────────────────────────────────────

    #setBtnText(btn, txt) {
      if (!btn) return;
      const slot = btn.querySelector('[data-mb-add-label]');
      if (slot) { slot.textContent = txt; return; }
      for (const node of btn.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()) {
          node.nodeValue = txt;
          return;
        }
      }
      btn.textContent = txt;
    }

    #updateUI(variant) {
      const r = this.#refs;
      const formatMoney = window.mbFormatMoney;

      if (!variant) {
        // Unavailable combination
        if (r.addBtn)    { r.addBtn.disabled = true;    r.addBtn.setAttribute('aria-disabled', 'true'); }
        if (r.stickyAdd) { r.stickyAdd.disabled = true; r.stickyAdd.setAttribute('aria-disabled', 'true'); }
        this.#setBtnText(r.addBtn,    'Unavailable');
        this.#setBtnText(r.stickyAdd, 'Unavailable');
        const pf = this.querySelector('product-form');
        if (pf) pf.dataset.state = 'unavailable';
        if (r.srStatus) r.srStatus.textContent = 'This combination is unavailable';
        document.dispatchEvent(new CustomEvent('mb:variant-change', { detail: { variant: null } }));
        return;
      }

      if (r.variantIdInput) r.variantIdInput.value = variant.id;

      // Price (main + sticky)
      const nowFormatted = formatMoney(variant.price);
      if (r.priceNow)    r.priceNow.textContent    = nowFormatted;
      if (r.stickyPrice) r.stickyPrice.textContent = nowFormatted;

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      // Math.floor, not round — Liquid's integer divided_by truncates, and the
      // first paint (Liquid) and this re-render must produce the same string.
      const pct = onSale
        ? Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)
        : 0;
      if (onSale) {
        const wasFormatted = formatMoney(variant.compare_at_price);
        if (r.priceWas) {
          r.priceWas.hidden = false;
          const s = r.priceWas.querySelector('s');
          if (s) { s.textContent = wasFormatted; s.setAttribute('aria-label', 'Was ' + wasFormatted); }
        }
        if (r.saveBadge)  r.saveBadge.textContent  = pct + '% off';
        if (r.saveAmount) r.saveAmount.textContent = 'You save ' + formatMoney(variant.compare_at_price - variant.price);
        if (r.stickyWas) {
          r.stickyWas.textContent = wasFormatted;
          r.stickyWas.hidden = false;
        }
      } else {
        if (r.priceWas)  r.priceWas.hidden = true;
        if (r.stickyWas) r.stickyWas.hidden = true;
      }
      // The panel (% off + "You save") shows/hides as one unit; the >= 1 gate
      // mirrors the Liquid show_save_panel guard (no "0% off" chips).
      if (r.savePanel) r.savePanel.hidden = !(onSale && pct >= 1);

      if (r.stickyVariant && variant.title !== 'Default Title') {
        r.stickyVariant.textContent = variant.title;
      }

      // Sticky thumbnail — variant.featured_image comes from `product.variants
      // | json`, whose `src` is the master CDN URL. Ask the CDN for a thumb-
      // sized render instead of shipping the full-size file into a 48px box.
      // Variants without their own image keep whatever is already displayed.
      const stickyImgSrc = variant.featured_image?.src;
      if (r.stickyImage && stickyImgSrc) {
        r.stickyImage.src = stickyImgSrc + (stickyImgSrc.includes('?') ? '&' : '?') + 'width=200';
      }

      // Availability
      const available = variant.available;
      if (r.addBtn) {
        r.addBtn.disabled = !available;
        r.addBtn.setAttribute('aria-disabled', String(!available));
      }
      this.#setBtnText(r.addBtn, available ? 'Add to cart' : 'Sold out');
      if (r.stickyAdd) {
        r.stickyAdd.disabled = !available;
        r.stickyAdd.setAttribute('aria-disabled', String(!available));
      }
      this.#setBtnText(r.stickyAdd, available ? 'Add to cart' : 'Sold out');

      const pf = this.querySelector('product-form');
      if (pf && pf.dataset.state !== 'loading' && pf.dataset.state !== 'added') {
        pf.dataset.state = available ? 'idle' : 'sold-out';
      }

      // Stock urgency. The exact count comes from the #inventory map (keyed by
      // variant id) emitted in Liquid, because product.variants|json omits
      // inventory_quantity. The map holds only low-stock shopify-managed
      // variants, so a hit means "show N left"; a miss means hide. (variant.id
      // is a number; JS coerces it to the map's string key on lookup.)
      if (r.stockEl && r.stockText) {
        const qty = this.#inventory[variant.id];
        if (qty > 0 && qty <= 3) {
          r.stockText.innerHTML = 'Only <strong>' + qty + ' left</strong> &mdash; ships in 2 business days';
          r.stockEl.removeAttribute('hidden');
        } else {
          r.stockEl.setAttribute('hidden', '');
        }
      }

      // SR announcement — reset then set on next frame so AT re-announces
      if (r.srStatus) {
        const msg = nowFormatted + (available ? ' — In stock' : ' — Sold out');
        r.srStatus.textContent = '';
        requestAnimationFrame(() => { r.srStatus.textContent = msg; });
      }

      // Option display labels ("Size: 8' x 10'"). Keyed off the option's own
      // position, not its render order — a product whose only option is Size
      // sits at position 1 and used to never update.
      r.optionLabels.forEach((el) => {
        const pos = parseInt(el.dataset.optionPosition, 10);
        el.textContent = variant['option' + pos] || '';
      });

      // Notify dependent widgets (bundle, etc.) of the new selection.
      // Carries the resolved variant object so listeners don't need to
      // scrape the DOM or re-parse the variant cache.
      document.dispatchEvent(new CustomEvent('mb:variant-change', { detail: { variant } }));
    }

    // ── Event wiring ───────────────────────────────────────────────────────

    #bindForm() {
      const onChange = (e) => {
        const inp = e.target;
        if (!inp.matches('[data-mb-option-input]')) return;
        const variant = this.#findVariant(this.#getSelectedOptions());

        // Update selected pill styles (for :has fallback in older browsers)
        this.#form.querySelectorAll('.mb-pdp-detail__size-label').forEach((lbl) => {
          const radio = lbl.querySelector('input[type="radio"]');
          if (radio) lbl.classList.toggle('is-selected', radio.checked);
        });

        this.#updateUI(variant);
        this.#updateOptionAvailability();

        // Sync ?variant=<id> into the URL so the address bar is shareable
        // and a refresh restores the selection. replaceState (not push) so
        // browser-back doesn't have to step through every option click.
        if (variant && variant.id && window.history && window.history.replaceState) {
          try {
            const u = new URL(window.location.href);
            u.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', u.toString());
          } catch (_) { /* old browsers / opaque origins — silently skip */ }
        }
      };

      this.#form.addEventListener('change', onChange);
      this.#cleanups.push(() => this.#form.removeEventListener('change', onChange));
    }

    /**
     * Shape chips. A chip shows only its shape's size pills (the others leave
     * the layout and the tab order) and checks the first one in stock; that
     * change runs through #bindForm like any size click, so price, gallery,
     * URL and sticky bar follow. Focus stays on the chip.
     */
    #bindShapes() {
      const group = this.querySelector('[data-mb-shape-group]');
      if (!group) return;

      const onChange = (e) => {
        const chip = e.target;
        if (!chip.matches('[data-mb-shape-input]')) return;
        const shape = chip.value;

        let first = null;
        this.#form.querySelectorAll('[data-mb-option-input][data-shape]').forEach((inp) => {
          const match = inp.dataset.shape === shape;
          inp.closest('label').hidden = !match;
          if (match && !first && !inp.disabled) first = inp;
        });
        if (first && !first.checked) {
          first.checked = true;
          first.dispatchEvent(new Event('change', { bubbles: true }));
        }

        group.querySelectorAll('.mb-pdp-detail__shape').forEach((lbl) => {
          lbl.classList.toggle('is-current', lbl.contains(chip));
        });
        const value = this.querySelector('[data-mb-shape-value]');
        if (value) value.textContent = shape;
      };

      group.addEventListener('change', onChange);
      this.#cleanups.push(() => group.removeEventListener('change', onChange));
    }

    /**
     * Move focus into `container`, and never let it fall to <body>.
     *
     * `:checked` alone is a trap: on a fully sold-out product every size radio
     * is `disabled`, including the checked one, and .focus() on a disabled
     * control silently does nothing — focus then sits on <body> with no visible
     * caret. So only ever aim at something actually focusable, and when the
     * container holds nothing focusable at all, park focus on the container
     * itself (tabindex="-1", the CLAUDE.md §2.8 pattern) so the next Tab
     * continues from where the customer is looking.
     */
    #focusInto(container) {
      if (!container) return false;
      const target = container.querySelector('[data-mb-option-input]:checked:not([disabled])')
                  // One combined selector, not a chain of them: querySelector
                  // returns the first match in *document* order, so focus lands
                  // on whatever the customer sees first. Chaining by element
                  // type instead sent footer handoffs to the newsletter button
                  // at the very bottom, off screen.
                  || container.querySelector(
                       '[data-mb-option-input]:not([disabled]), button:not([disabled]), a[href],'
                       + ' input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
                     );
      if (target) {
        target.focus({ preventScroll: true });
        return true;
      }
      if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      container.focus({ preventScroll: true });
      return document.activeElement === container;
    }

    /**
     * Sticky bar's "Change <option>" button. Lives outside this element (the
     * bar is position:fixed), names its target group in aria-controls, and
     * expects to land the customer *in* that radiogroup — not merely near it.
     * Focus moves first with preventScroll so the smooth scroll below owns the
     * final position; without it the browser's own focus scroll fights it and
     * the group ends up jammed against the viewport edge.
     */
    #bindStickyChange() {
      const btn = document.querySelector('[data-mb-sticky-change]');
      if (!btn) return;

      const onClick = () => {
        const groupId = btn.getAttribute('aria-controls');
        const group = groupId ? document.getElementById(groupId) : null;
        if (!group) return;

        this.#focusInto(group);

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        group.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      };

      btn.addEventListener('click', onClick);
      this.#cleanups.push(() => btn.removeEventListener('click', onClick));
    }

    /**
     * Sticky bar visibility. The bar hides whenever something it would get in
     * the way of is on screen:
     *   • the buy box it duplicates — the option pickers and the CTA
     *   • the footer, which it would otherwise cover at the bottom of the page
     *     (reserving page-bottom padding instead left a visible empty strip)
     * and slides back in once all of them have scrolled away.
     *
     * Every anchor is observed by one observer and the intersecting ones are
     * kept in a set, because "is anything still on screen?" can't be answered
     * from a single entry: a callback fires only for the targets that changed.
     *
     * The bottom rootMargin matches the bar's measured height — an anchor
     * sitting *underneath* the fixed bar is covered, not visible, and should
     * still count as out of view. No oscillation risk: the bar is
     * position:fixed, so toggling it never reflows the anchors it observes.
     *
     * The bar is rendered hidden (see the snippet), so a page where this never
     * runs simply has no sticky bar instead of one welded over the footer. A
     * browser that has JS but no IntersectionObserver gets it unconditionally
     * visible — the old behaviour, better than nothing on a buy button.
     */
    #bindStickyVisibility() {
      const bar = document.querySelector('[data-mb-sticky-cta]');
      if (!bar) return;
      const revealUnconditionally = () => bar.removeAttribute('data-mb-sticky-hidden');
      if (!('IntersectionObserver' in window)) { revealUnconditionally(); return; }

      const anchors = [
        ...this.querySelectorAll('.mb-pdp-detail__option'),
        this.querySelector('.mb-pdp-detail__actions') || this.#refs.addBtn,
        // Outside this element, unlike the rest — the footer belongs to the
        // layout, not the product form. This theme renders footer.mb-footer;
        // the bare `footer` is the fallback for Dawn's stock markup.
        document.querySelector('footer.mb-footer') || document.querySelector('footer'),
      ].filter(Boolean);
      if (!anchors.length) { revealUnconditionally(); return; }

      const onScreen = new Set();
      const onIntersect = (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) onScreen.add(e.target);
          else onScreen.delete(e.target);
        });

        const hide = onScreen.size > 0;
        // Never drop focus to <body>: if the customer is inside the bar when it
        // goes away, hand focus to the first control of whatever pushed it out.
        // It has to be one in an *on-screen* anchor — handing focus to the CTA
        // while only the option pickers are in view would park the caret on an
        // element the customer can't see.
        if (hide && bar.contains(document.activeElement)) {
          const inView = anchors.find((el) => onScreen.has(el));
          if (!this.#focusInto(inView)) this.#refs.addBtn?.focus({ preventScroll: true });
        }
        bar.toggleAttribute('data-mb-sticky-hidden', hide);
      };

      // The bar's height is content-dependent (a long variant title, a merchant
      // font, a bigger text-zoom setting all move it), so measure it instead of
      // hard-coding: --mb-sticky-cta-h feeds the reserved page-bottom padding in
      // modern-product.css, the same number feeds rootMargin here. offsetHeight,
      // not the bounding rect, so the translateY the bar hides with can't skew it.
      // rootMargin is fixed at construction, so a changed height means a new
      // observer — cheap, and it only happens on real resize.
      let io = null;
      let measured = -1;
      const syncMetrics = () => {
        const h = bar.offsetHeight;
        if (h === measured) return;
        measured = h;
        document.documentElement.style.setProperty('--mb-sticky-cta-h', `${h}px`);
        io?.disconnect();
        onScreen.clear();
        io = new IntersectionObserver(onIntersect, {
          rootMargin: `0px 0px -${h}px 0px`,
          threshold: 0,
        });
        anchors.forEach((el) => io.observe(el));
      };

      syncMetrics();

      // No ResizeObserver → the first measurement stands. Still better than a
      // constant: it's this page's real bar, just not re-measured on resize.
      const ro = 'ResizeObserver' in window ? new ResizeObserver(syncMetrics) : null;
      ro?.observe(bar);

      this.#cleanups.push(() => {
        ro?.disconnect();
        io?.disconnect();
        document.documentElement.style.removeProperty('--mb-sticky-cta-h');
      });
    }

    #bindQty() {
      const minusBtn = this.querySelector('[data-mb-qty-minus]');
      const plusBtn  = this.querySelector('[data-mb-qty-plus]');
      const display  = this.#refs.qtyDisplayInput;
      const hidden   = this.#refs.qtyHiddenInput;
      if (!minusBtn || !plusBtn || !display || !hidden) return;

      const sync = (val) => {
        const n = Math.max(1, val);
        display.value = n;
        hidden.value = n;
      };
      const onMinus  = () => sync(parseInt(display.value, 10) - 1);
      const onPlus   = () => sync(parseInt(display.value, 10) + 1);
      const onChange = () => sync(parseInt(display.value, 10));

      minusBtn.addEventListener('click', onMinus);
      plusBtn.addEventListener('click', onPlus);
      display.addEventListener('change', onChange);
      this.#cleanups.push(() => {
        minusBtn.removeEventListener('click', onMinus);
        plusBtn.removeEventListener('click', onPlus);
        display.removeEventListener('change', onChange);
      });
    }
  }
  customElements.define('mb-variant-picker', MBVariantPicker);
}
